import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => {
    const t = getInitialTarget();
    setTarget(t);
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(24 * 60 * 60 * 1000);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = 24 * 60 * 60 * 1000 - remaining;
  const progress = Math.min(90, 65 + elapsed / (24 * 60 * 60 * 1000) * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-white border border-red-500/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(239,68,68,0.1)]" data-id="element-41" data-mp="1pzelbtd0hfdi">
      <div className="text-center" data-id="element-42" data-mp="2sby4avm38fd4">
        <div className="flex items-center justify-center gap-2 text-red-600 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-43" data-mp="2ndwdfoif7br3">
          <AlarmClock size={20} className="animate-pulse" data-id="element-44" data-mp="2jaq7khz7zml5" />
          <span data-id="element-45" data-mp="2ed877fz9qfor">El precio de lanzamiento termina en:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-46" data-mp="2efax800f88th">
          <TimeBox value={pad(totalHours)} label="hs" data-id="element-47" data-mp="29f6f65jv7615" />
          <span className="text-3xl md:text-5xl font-bold text-red-500" data-id="element-48" data-mp="24mn4phgh4r0k">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-49" data-mp="1zo22xdlkpza0" />
          <span className="text-3xl md:text-5xl font-bold text-red-500" data-id="element-50" data-mp="1zolhqlfxg7jt">:</span>
          <TimeBox value={pad(seconds)} label="seg" data-id="element-51" data-mp="1vni2o15pefjn" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-slate-600 mb-8" data-id="element-52" data-mp="1qn44rbxtglzc">
          Después sube el precio y no vuelve a bajar.
        </p>

        <div className="space-y-2" data-id="element-53" data-mp="1lqdrewdec4hm">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-54" data-mp="16iaz6e7ched">
            <span className="text-red-600" data-id="element-55" data-mp="64tl7i8estx4">Cupos al precio de lanzamiento</span>
            <span className="text-red-500" data-id="element-56" data-mp="b4fy1qkbs85e">Quedan pocos</span>
          </div>
          <div className="w-full h-2.5 bg-red-100 rounded-full overflow-hidden border border-red-200" data-id="element-57" data-mp="b2bksi0jdwsg">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `74%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.4)]" data-id="element-58" data-mp="f3ouj36r2k4n" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-red-600 font-medium" data-id="element-59" data-mp="2vsu9w5zeqq81">
            74% de los cupos tomados
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-cream border-2 border-red-500/30 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-inner" data-id="element-60" data-mp="2btvhdhyja38a">
      <div className="flex items-baseline justify-center gap-1" data-id="element-61" data-mp="2qj9mgdv2tyhv">
        <span className="text-4xl md:text-5xl font-bold text-slate-900 tabular-nums" data-id="element-62" data-mp="2lp5je5oxmam2">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-red-600" data-id="element-63" data-mp="2zk7ur8cjss69">
          {label}
        </span>
      </div>
    </div>;
}